import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { fetchPublicRound, PublicRound } from '../services/publicPagesApi';
import { fmtDate as fmtDateShared } from '../utils/format';
import { publicBodyStyle, publicCardStyle } from './shared/PublicPage';

/**
 * Rond via länk, utan inloggning: alla platser i ronden med status. Man trycker på en
 * plats, fyller i punkterna på kontrollsidan och kommer tillbaka hit. ?c=<kontakt-id>
 * följer med till varje plats, så namnet fylls i och "påbörjad av" fungerar per plats.
 */

const bodyStyle: React.CSSProperties = { ...publicBodyStyle, padding: '16px' };
const cardStyle: React.CSSProperties = { ...publicCardStyle(640), padding: '24px' };
const fmtDate = (d: string | null) => fmtDateShared(d, { month: 'long' });

const STATUS: Record<string, [string, string, string]> = {
    // status → [text, bakgrund, textfärg]
    pending:  ['Kvar',          '#e8f0fe', '#1a4aa6'],
    overdue:  ['Försenad',      '#f8d7da', '#721c24'],
    done:     ['Klar',          '#d1e7dd', '#0f5132'],
    action:   ['Åtgärd väntar', '#fff3cd', '#664d03'],
    skipped:  ['Hoppades över', '#e2e3e5', '#41464b'],
};

const RoundPage: React.FC = () => {
    const { token } = useParams<{ token: string }>();
    const [searchParams] = useSearchParams();
    const contactId = useMemo(() => { const n = Number(searchParams.get('c')); return Number.isInteger(n) && n > 0 ? n : null; }, [searchParams]);
    const [data, setData] = useState<PublicRound | null>(null);
    const [loading, setLoading] = useState(true);
    const [notFound, setNotFound] = useState(false);

    useEffect(() => {
        if (!token) return;
        fetchPublicRound(token, contactId)
            .then(setData)
            .catch(err => { if (err?.response?.status === 404) setNotFound(true); })
            .finally(() => setLoading(false));
    }, [token, contactId]);

    if (loading) return <div style={bodyStyle}><div style={cardStyle}><p style={{ color: '#888' }}>Laddar…</p></div></div>;
    if (notFound || !data) {
        return (
            <div style={bodyStyle}><div style={cardStyle}>
                <h1 style={{ color: '#c0392b', fontSize: 22 }}>Länken hittades inte</h1>
                <p style={{ color: '#666' }}>Länken till ronden är ogiltig.</p>
            </div></div>
        );
    }

    const allDone = data.done >= data.total;
    const awaitingAction = data.items.filter(i => i.status === 'action').length;
    const suffix = contactId ? `?c=${contactId}` : '';

    return (
        <div style={bodyStyle}>
            <div style={cardStyle}>
                <div style={{ fontSize: 12, color: '#888', textTransform: 'uppercase', letterSpacing: 1 }}>Egenkontroll · rond</div>
                <h1 style={{ margin: '4px 0 4px', fontSize: 22 }}>{data.schedule_name}</h1>
                <div style={{ color: '#666', fontSize: 14, marginBottom: 14 }}>
                    {data.template_name} · {data.total} platser · ska vara gjord senast {fmtDate(data.due_date)}
                </div>

                <div style={{ background: allDone ? '#d1e7dd' : '#e8f0fe', color: allDone ? '#0f5132' : '#1a4aa6', borderRadius: 8, padding: 12, marginBottom: 14, fontSize: 15 }}>
                    {allDone
                        ? <>Alla {data.total} platser är gjorda. Tack!</>
                        : <><b>{data.done}</b> av <b>{data.total}</b> platser är klara{awaitingAction > 0 ? `, ${awaitingAction} väntar på åtgärd` : ''}. Tryck på en plats för att göra den.
                            {data.group_owned && <div style={{ fontSize: 13, marginTop: 4 }}>Ronden går till alla i gruppen {data.responsible_group_name ?? ''}. Den som påbörjar en plats gör klart den; de andra ser vem som håller på.</div>}
                          </>}
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {data.items.map(item => {
                        const [text, bg, fg] = STATUS[item.status] ?? [item.status, '#eee', '#333'];
                        const open = item.status === 'pending' || item.status === 'overdue';
                        const mine = !!data.viewer && item.started_by_contact_id === data.viewer.id;
                        const who = open && item.started_by_name
                            ? (mine ? 'du håller på' : `${item.started_by_name} håller på`)
                            : !open && item.completed_by_name ? `av ${item.completed_by_name}` : null;
                        return (
                            <Link key={item.id} to={`/inspection/${item.token}${suffix}`}
                                style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, padding: '12px 14px',
                                         border: '1px solid #ddd', borderRadius: 8, textDecoration: 'none', color: '#222', background: open ? 'white' : '#fafafa' }}>
                                <div>
                                    <div style={{ fontWeight: 600, fontSize: 15 }}>{item.zone_name ?? 'Plats'}</div>
                                    {who && <div style={{ fontSize: 12, color: '#777' }}>{who}{item.has_deviation ? ' · avvikelse' : ''}</div>}
                                    {!who && item.has_deviation && <div style={{ fontSize: 12, color: '#777' }}>avvikelse</div>}
                                    {!open && item.no_need && <div style={{ fontSize: 12, color: '#777' }}>utfört, inget behov</div>}
                                </div>
                                <span style={{ background: bg, color: fg, borderRadius: 12, padding: '4px 10px', fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap' }}>
                                    {open && item.has_draft && !item.started_by_name ? 'Påbörjad' : text}
                                </span>
                            </Link>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};

export default RoundPage;
